import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ShoppingCart } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { useLocalShopPrices, useDeleteLocalShopPrice } from '@/hooks/use-local-shop-prices';
import { LocalShopPriceFilters, LocalShopPrice } from '@/types/database/local-shop-prices';
import LocalShopPricesHeader from '@/components/local-shops/LocalShopPricesHeader';
import LocalShopPricesFilter from '@/components/local-shops/LocalShopPricesFilter';
import LocalShopPricesTable from '@/components/local-shops/LocalShopPricesTable';
import LocalShopPriceForm from '@/components/local-shops/LocalShopPriceForm';
import LocalShopUpload from '@/components/local-shops/upload/LocalShopUpload';
import LocalShopUploadNew from '@/components/local-shops/upload/LocalShopUploadNew';

const LocalShops: React.FC = () => {
  const [filters, setFilters] = useState<LocalShopPriceFilters>({});
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [editingPrice, setEditingPrice] = useState<LocalShopPrice | null>(null);

  const { data: prices = [], isLoading, refetch } = useLocalShopPrices(filters);
  const deleteLocalShopPrice = useDeleteLocalShopPrice();

  const handleFiltersChange = (newFilters: LocalShopPriceFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const handleAddNew = () => {
    setEditingPrice(null);
    setShowAddDialog(true);
  };

  const handleUpload = () => {
    setShowUploadDialog(true);
  };

  const handleEdit = (price: LocalShopPrice) => {
    setEditingPrice(price);
    setShowAddDialog(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this local shop price?')) {
      await deleteLocalShopPrice.mutateAsync(id);
    }
  };

  const handleFormSuccess = () => {
    setShowAddDialog(false);
    setEditingPrice(null);
    refetch();
  };

  const handleUploadSuccess = () => {
    setShowUploadDialog(false);
    refetch();
  };

  return (
    <Layout>
      <div className="container mx-auto p-6">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <ShoppingCart className="h-8 w-8 text-purple-600" />
            <h1 className="text-3xl font-bold text-gray-900">Local Shop Prices</h1>
          </div>
          <p className="text-gray-600">
            Collect pricing from local shop sources
          </p>
        </div>

        <div className="space-y-6">
          <LocalShopPricesHeader
            onAddNew={handleAddNew}
            onUpload={handleUpload}
          />

          <LocalShopPricesFilter
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClearFilters={handleClearFilters}
          />

          <Card>
            <CardHeader>
              <CardTitle>Local Shop Prices</CardTitle>
            </CardHeader>
            <CardContent>
              <LocalShopPricesTable
                prices={prices}
                onEdit={handleEdit}
                onDelete={handleDelete}
                isLoading={isLoading}
              />
            </CardContent>
          </Card>
        </div>

        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingPrice ? 'Edit Local Shop Price' : 'Add New Local Shop Price'}
              </DialogTitle>
            </DialogHeader>
            <LocalShopPriceForm
              initialData={editingPrice}
              onSuccess={handleFormSuccess}
              onCancel={() => setShowAddDialog(false)}
            />
          </DialogContent>
        </Dialog>

        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Upload Local Shop Prices</DialogTitle>
            </DialogHeader>
            <LocalShopUploadNew
              onSuccess={handleUploadSuccess}
              onCancel={() => setShowUploadDialog(false)}
            />
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default LocalShops;
